// /in-licensor-deck — Public, shareable corporate deck for IN-LICENSORS
// (pharma / specialty pharma / regional commercial partners looking to
// bring in external assets). Sent as a follow-up link after BD
// conversations. NO password gate. NO client name leakage. Pitches
// ForesightRx as the curated deal-flow + diligence layer for pharma
// BD teams, with flexible commercial models (retainer / success-based /
// hybrid). PDF export via /api/public/in-licensor-deck.pdf.
// Sep 2026 · editorial refinement: sharper wording, clearer company- vs
// individual-level mapping, and a "Proof in practice" slide that
// repositions the two most relevant existing /case-studies engagements.
import React from 'react';
import {
  Globe2, Stethoscope, Layers, Target, Calendar,
  Sparkles, Handshake, DollarSign, Repeat, Radar, MapPin, Building2,
} from 'lucide-react';
import { DeckShell } from '@/components/deck/DeckShell';

const SLIDES = [
  // ─── 1 · Title ──────────────────────────────────────
  {
    kind: 'cover',
    eyebrow: 'ForesightRx · For In-Licensors',
    title: 'Curated, de-risked in-licensing deal flow — matched to your pipeline gaps.',
    sub: 'ForesightRx is an AI-enabled, human-validated intelligence layer over 14,000+ biotech assets, refreshed from 12+ global partnering conferences a year. We map assets to your therapeutic thesis, geography and stage — at company level and at the level of the individual BD owner — pre-screen them for science, IP, payer readiness and rights availability, and stay in the room from first introduction through diligence to signing.',
    foot: 'A short, honest brief · 10 slides',
  },

  // ─── 2 · The Problem ───────────────────────────────
  {
    kind: 'two-col',
    eyebrow: 'The Problem',
    title: 'Search & Evaluation teams drown in noise — and still miss the right asset.',
    leftTitle: 'What most pharma BD and S&E teams are stuck doing',
    leftBullets: [
      'Paying for static databases that are already six months stale on the day you log in.',
      'Triaging hundreds of unsolicited biotech decks — 95% of them off-thesis.',
      'Flying to 4–6 conferences a year and meeting whoever accepted the invite, not whoever fits.',
      'Re-running diligence from scratch on every dossier, with a different yardstick each time.',
    ],
    rightTitle: 'What actually fills a pipeline gap',
    rightBullets: [
      'A continuously refreshed view of who is actively out-licensing — asset by asset, geography by geography.',
      'Assets filtered against YOUR inclusion and exclusion criteria before they ever reach your inbox.',
      'The same 8-dimension scoring on every dossier, so cohorts are directly comparable.',
      'Payer / HTA readiness (EU JCA, US AMCP) surfaced at first look, not after the term sheet.',
    ],
    closer: 'We built ForesightRx to turn in-licensing from an inbound lottery into a repeatable, criteria-driven search-and-evaluation process.',
  },

  // ─── 3 · Proof points ──────────────────────────────
  {
    kind: 'stats',
    eyebrow: 'The Living Intelligence Layer',
    title: 'Databases are snapshots. This is alive.',
    sub: 'Every other partnering platform gives you a point-in-time export. ForesightRx is updated from the ground — every conference, every meeting, every qualified BD conversation — and validated by our team, so what you read today is current and defensible in front of your committee.',
    stats: [
      { value: '14,000+', label: 'Biotech assets indexed' },
      { value: '4,250+', label: 'Biotech companies' },
      { value: '2,800+', label: 'BD contacts mapped' },
      { value: '20+', label: 'Commercial partners' },
    ],
    pillars: [
      { icon: <Calendar className="w-4 h-4" />, title: '12+ conferences a year', desc: 'BIO International, BIO-Europe, ChinaBio, CPHI, JPM and more — our team is on the floor, so the index reflects who is genuinely out-licensing right now, not who was a year ago.' },
      { icon: <Globe2 className="w-4 h-4" />, title: 'EU JCA / HTA pre-read', desc: 'In-scope oncology, ATMP and cell & gene assets are pre-screened for Joint Clinical Assessment readiness before you bid.' },
      { icon: <Stethoscope className="w-4 h-4" />, title: 'US AMCP / payer pre-read', desc: 'AMCP dossier readiness, ICER signal, RWE plan and likely payer objections — surfaced at diligence stage, not after.' },
    ],
  },

  // ─── 4 · How it works ──────────────────────────────
  {
    kind: 'process',
    eyebrow: 'How it works',
    title: 'Your criteria in. Validated assets out. The same yardstick every time.',
    sub: 'You define the therapeutic areas, modalities, stages, geographies and deal structures you will actually transact on. Our AI mapping engine applies those rules deterministically across the live index — at company level (which out-licensors fit) and at individual level (which BD owner to approach) — and our team validates, enriches and scores every survivor before it reaches you.',
    steps: [
      { n: '1', title: 'Criteria intake',            desc: 'A 45-minute working session to capture your in-licensing thesis: TAs and sub-indications, modality preferences, stage floor, territories, exclusions — and what a "no" looks like.' },
      { n: '2', title: 'Deterministic screen',      desc: 'Your rules run against 14,000+ assets as inclusion and exclusion logic, not keyword search. Off-thesis assets never reach you; assets you have already reviewed are suppressed.' },
      { n: '3', title: 'Validate, enrich & score',  desc: 'Every survivor is human-validated, enriched with live web evidence and scored across 8 dimensions: clinical, regulatory, IP, competitive, commercial, strategic fit, deal readiness and risk. Rights availability is confirmed before the asset is shown to you.' },
      { n: '4', title: 'Warm, gated introduction',  desc: 'We already know the out-licensor and the right individual. On your "yes", we open the conversation, run the NDA step, coordinate the data room, schedule the first scientific meeting and stay through negotiation to signing.' },
    ],
    closer: 'Nothing reaches your team that has not passed your own rules first. The shortlist is short on purpose.',
  },

  // ─── 5 · What you receive ──────────────────────────
  {
    kind: 'deliverables',
    eyebrow: 'What you receive',
    title: 'Structured, decision-ready intelligence — not a login to a database.',
    items: [
      { icon: <Layers />,     title: 'Scored asset dossiers',            desc: 'One-page BD memos per asset: science, MoA, stage, competitive landscape, IP position, payer read and likely deal structure — plus the primary internal objection and the evidence that would overcome it.' },
      { icon: <Target />,     title: 'Buyer-specific shortlists',        desc: 'Sharply scoped lists for a single corporate-development thesis — EU commercialisation-ready, China-origin pipeline, rare-disease bolt-ons — mirroring your own BD deck criteria and weights, and mapped to the company and the named BD counterpart.' },
      { icon: <Sparkles />,   title: 'Committee Intelligence Workspace', desc: 'Bi-monthly cohorts of 25–40 in-scope assets, evaluated by your 6-role committee in a blinded Delphi review inside a private client portal, with a math-derived In-License / Reassess / Decline verdict.' },
      { icon: <Radar />,      title: 'Conference BD Command',            desc: 'Pre-event prioritised target lists, counterpart dossiers, scheduled meetings, same-day flash reports and a structured 5-day post-conference follow-up.' },
    ],
  },

  // ─── 6 · Sourcing footprint ────────────────────────
  {
    kind: 'regional',
    eyebrow: 'Sourcing footprint',
    title: 'Global origin. Rights sliced the way you transact.',
    sub: 'Half the value in cross-border licensing sits in the geographies most pharma BD teams never visit. We index out-licensors across Asia, Europe, the Americas and emerging markets — and map every asset by the rights that are actually available, not just by molecule.',
    left: {
      icon: <Globe2 />,
      title: 'Where the assets come from',
      bullets: [
        'China, Korea, Japan, India and Australia — clinical-stage assets with regional data and global or ex-Asia rights available.',
        'European mid-caps and academic spin-outs with EU data packages and US/Asia rights unencumbered.',
        'US biotechs seeking regional partners for ex-US commercialisation.',
      ],
    },
    right: {
      icon: <MapPin />,
      title: 'How rights are matched to you',
      bullets: [
        'Global, regional or single-territory in-licensing — the mapper works by available rights, not just by asset.',
        'Regional leaders see the carve-outs global players cannot compete for; global players see only assets with worldwide rights intact.',
        'Already-partnered territories are flagged before any introduction, so you never chase encumbered rights.',
      ],
    },
    closer: 'You define the territory and deal structure you will transact on. We bring only the out-licensors who can actually grant it.',
  },

  // ─── 7 · Proof in practice (existing /case-studies, repositioned) ──
  {
    kind: 'regional',
    eyebrow: 'Proof in practice',
    title: 'The same operating model, already running for pharma BD teams.',
    sub: 'Two anonymised engagements from our published case studies — one European, one cross-border China-to-West — showing how criteria-driven screening, rights validation and on-the-ground BD translate into a working in-licensing pipeline.',
    left: {
      icon: <Building2 />,
      title: 'European Specialty Pharma · EU commercialisation-ready shortlist across 25+ markets',
      bullets: [
        'The need: a continuously refreshed shortlist of in-licensing-ready assets that fit their EU commercialisation muscle — deck-aligned and deterministically scored, with the payer story pre-flagged, not another sprawling database.',
        'Our approach: 12 selection rules from the client\'s corporate-development deck encoded into the scoring engine; live web enrichment with cited sources; Tier 1 / Tier 2 / Watchlist fit tiering; EU-rights status and JCA-readiness flags on every in-scope oncology / ATMP asset.',
        'Delivered as a monthly branded Excel export plus a live workspace for the BD team.',
        'Outcome: BD now opens diligence only on assets that have already cleared the internal deck criteria and have a credible payer pathway. Wasted-meeting rate dropped materially within the first quarter.',
      ],
    },
    right: {
      icon: <Globe2 />,
      title: 'US Specialty Pharma · China-origin pipeline into Western markets',
      bullets: [
        'The need: a continuous flow of differentiated China-origin assets (oncology, immunology, rare disease) with Western rights available — off-the-shelf databases surfaced Chinese pipelines weeks, sometimes quarters, after the window had closed.',
        'Our approach: a living shortlist sourced from NMPA filings, ChinaBio Partnering Forum and CPIC Shanghai floor coverage, WeChat / vernacular signal channels and direct BD relationships in Shanghai, Beijing, Suzhou and Guangzhou; every asset pre-screened for EU rights availability, JCA readiness and US AMCP / payer-value plausibility.',
        'On-site BD at four China-focused conferences a year with one-on-one meeting facilitation and same-day flash reports; shortlist refreshed weekly.',
        'Outcome: the client now sees China-origin assets weeks before they surface in English-language databases — and only evaluates those with a credible Western payer pathway already pre-flagged.',
      ],
    },
    closer: 'Full anonymised case studies — including our emerging-biotech out-licensing engagement — at foresightrx.com/case-studies.',
  },

  // ─── 8 · Commercial models ─────────────────────────
  {
    kind: 'options',
    eyebrow: 'Ways to work with us',
    title: 'Three commercial models. You choose the risk profile.',
    sub: 'Some teams want a predictable, productised intelligence programme. Others want to pay only when a deal signs. Most land in between. All three run on the same engine, the same scoring and the same people.',
    options: [
      {
        icon: <Repeat className="w-5 h-5" />,
        tag: 'Flagship',
        title: 'Retained programme',
        bestFor: 'For BD teams that want a continuous, committee-ready pipeline.',
        bullets: [
          '12-month productised engagement calibrated to one geography or thesis.',
          'Quarterly or bi-monthly intelligence reports: scored dossiers, fresh asset cohorts, conference coverage.',
          'Dedicated platform workspace, copilot and dashboard for your team.',
          'Success fee waived or reduced on deals sourced through the programme.',
        ],
      },
      {
        icon: <DollarSign className="w-5 h-5" />,
        tag: 'Zero upfront',
        title: 'Success-based',
        bestFor: 'For teams that want curated deal flow with no fixed cost.',
        bullets: [
          'No retainer. No subscription. No platform fee.',
          'We share only assets that pass your criteria; you engage the ones you like.',
          'Standard market success fee, payable only on a signed deal with a counter-party we introduced.',
          'Non-exclusive — runs alongside your in-house BD and other advisors.',
        ],
      },
      {
        icon: <Handshake className="w-5 h-5" />,
        tag: 'Most common',
        title: 'Hybrid',
        bestFor: 'For teams that want depth on a thesis and skin in the game from us.',
        bullets: [
          'Light retainer covering a scoped shortlist, a conference, or a bi-monthly cohort.',
          'Reduced success fee on any deal that closes from that scope.',
          'Scale up or down between cycles — no annual lock-in.',
          'Ideal for a first engagement before committing to the full programme.',
        ],
      },
    ],
    closer: 'Every model is non-exclusive and starts with the same 45-minute criteria session. Pricing is scoped to geography, number of theses and conference coverage — we put a proposal in writing within a week.',
  },

  // ─── 9 · Engagement timeline ───────────────────────
  {
    kind: 'engagement',
    eyebrow: 'How we work together',
    title: 'First validated shortlist inside two weeks.',
    phases: [
      { phase: 'Week 0',            title: 'Criteria session + model selection',          desc: '45 minutes with your BD lead. We capture the thesis, exclusions, territories and stage floor, and agree the commercial model. NDA-free until you want to see confidential data.' },
      { phase: 'Week 1–2',          title: 'First screened, scored shortlist',            desc: 'Your rules run across the live index. You receive the first cohort of validated, scored dossiers plus a private workspace link showing exactly what was screened out and why.' },
      { phase: 'Week 3+',           title: 'Introductions, meetings and data rooms',      desc: 'For every "yes", we open the conversation with the out-licensor\'s BD owner, coordinate NDAs and the data room, and schedule the first scientific meeting.' },
      { phase: 'Ongoing',           title: 'Cohorts, conferences, committee, deal progression', desc: 'Fresh cohorts on your cadence, live conference coverage, committee Delphi reviews, and deal support through term sheet and signing. The index keeps refreshing underneath.' },
    ],
    closer: 'Your suppression list travels with you — declined assets and already-partnered biotechs never resurface unless something material changes.',
  },

  // ─── 10 · CTA ──────────────────────────────────────
  {
    kind: 'cta',
    eyebrow: 'Next step',
    title: 'Tell us your in-licensing criteria.',
    sub: 'Share your therapeutic areas, modalities, stage floor and territories. Within one week we will come back with an initial screened read of the live index — how many assets pass your rules today, and a sample of scored dossiers — at no cost and no commitment.',
    primary: { label: 'Tell us your in-licensing criteria', to: '/apply?intent=in_licensor' },
    secondary: { label: 'Email · enquiry@foresightrx.com', href: 'mailto:enquiry@foresightrx.com' },
    foot: 'ForesightRx · Living intelligence for pharma in-licensing. Singapore · India · Global.',
  },
];

export default function InLicensorDeckPage() {
  return (
    <DeckShell
      slides={SLIDES}
      pdfPath="/api/public/in-licensor-deck.pdf"
      pdfFilename="ForesightRx_In-Licensor_Deck.pdf"
      docTitle="For Pharma In-Licensors · ForesightRx"
      testId="in-licensor-deck-page"
    />
  );
}
